import { Button, Drawer, message } from "antd"
import { useEffect, useMemo, useState } from "react"
import CreationForm from "./CreationForm"
import { FileOutlined } from "@ant-design/icons"
import { calculateTotalPlateCost } from "../../../components/Calculation/calculateTotalPlateCosts"
import { doc, getDoc, getDocs, query, where, documentId, collection } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { getBaseComment } from "./getBaseComment"
import { calculateVat } from "../../../components/Orders/Helpers/CalculateVat"

export default function CreateInvoiceAsDrawer({
  selectedRows,
  selectedRowKeys,
  resetRows,
  forProforma = false,
  editingInvoiceData,
  onDrawerClose,
  openDrawerInitially = false,
}) {
  const [open, setOpen] = useState(openDrawerInitially) // Use it here
  const [objectFromDrawer, setObjectFromDrawer] = useState([])
  const [clientData, setClientData] = useState({})
  const [extractedManufacturer, setExtractedManufacturer] = useState(null)
  const [fromDrawer, setFromDrawer] = useState(true)
  const [loading, setLoading] = useState(false)
  const [hasProformas, setHasProformas] = useState(false)
  const [proformasData, setProformasData] = useState({})
  const [showWeight, setShowWeight] = useState(false)

  const showDrawer = () => {
    setFromDrawer(false)
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
    if (onDrawerClose) {
      onDrawerClose() // Call the callback passed from AccountingInvoices
    }
    // Do NOT call resetRows here if editing, parent will handle refresh.
    // Only call resetRows if it's for the original purpose (clearing selection table)
    if (!editingInvoiceData && resetRows) {
      resetRows()
    }
  }

  useEffect(() => {
    let active = true

    const loadData = () => {
      if (!active) return
      if (editingInvoiceData && open) {
        setLoading(true)
        try {
          // 1. Directly use prices from editingInvoiceData
          const preloadedPrices = editingInvoiceData.prices.map((item, index) => {
            const originalStoredComment = item.productComment || ""
            // Use the item's specific weight for more accurate stripping
            const baseStoredComment = getBaseComment(originalStoredComment, item.weight)
            return {
              ...item,
              key: item.key !== undefined ? item.key : `edit-${index}`,
              weight: item.weight || 0,
              boxesCount: item.boxesCount || 0,
              unit: item.unit || "thous.",
              productComment: originalStoredComment, // Keep original for some displays if needed
              rawComment: baseStoredComment, // Store the CLEAN base comment
            }
          })
          setObjectFromDrawer(preloadedPrices)

          // 2. Fetch CURRENT client and manufacturer data (not the old data stored in invoice)
          // This ensures we get the up-to-date bank accounts and other details
          const clientId = editingInvoiceData.clientId || editingInvoiceData.client?.id
          const manufacturerId = editingInvoiceData.manufacturer?.id

          let clientPromise = Promise.resolve(editingInvoiceData.client)
          let manufacturerPromise = Promise.resolve(editingInvoiceData.manufacturer)

          if (clientId) {
            clientPromise = getDoc(doc(db, "clients", clientId)).then((snap) =>
              snap.exists() ? { ...snap.data(), id: snap.id } : editingInvoiceData.client
            )
          }

          if (manufacturerId) {
            manufacturerPromise = getDoc(doc(db, "manufacturers", manufacturerId)).then((snap) =>
              snap.exists() ? { ...snap.data(), id: snap.id } : editingInvoiceData.manufacturer
            )
          }

          Promise.all([clientPromise, manufacturerPromise])
            .then(([currentClient, currentManufacturer]) => {
              if (active) {
                setClientData(currentClient)
                let manufacturer = currentManufacturer
                manufacturer.logoUrl =
                  manufacturer.logoUrl ||
                  "https://firebasestorage.googleapis.com/v0/b/managers-testing.appspot.com/o/manufacturers%2FdefaultLogo.png?alt=media&token=bdbe0c13-2de9-4be7-bc80-be8fd7e0f82a"
                setExtractedManufacturer(manufacturer)
              }
            })
            .catch(() => {
              // Fallback to invoice data if fetch fails
              if (active) {
                setClientData(editingInvoiceData.client)
                let manufacturer = editingInvoiceData.manufacturer
                manufacturer.logoUrl =
                  manufacturer.logoUrl ||
                  "https://firebasestorage.googleapis.com/v0/b/managers-testing.appspot.com/o/manufacturers%2FdefaultLogo.png?alt=media&token=bdbe0c13-2de9-4be7-bc80-be8fd7e0f82a"
                setExtractedManufacturer(manufacturer)
              }
            })
          setFromDrawer(true) // Important for CreationForm

          // 3. Handle proforma data if this invoice was created from proformas
          if (
            editingInvoiceData.proformaReferenceIds &&
            editingInvoiceData.proformaReferenceIds.length > 0
          ) {
            // Fetch proforma details and calculate totals
            const proformasRef = collection(db, "proformas")
            const proformasQuery = query(
              proformasRef,
              where(
                documentId(),
                "in",
                editingInvoiceData.proformaReferenceIds
              )
            )

            getDocs(proformasQuery)
              .then((proformasSnapshot) => {
                if (!active) return
                const fetchedProformas = proformasSnapshot.docs.map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                }))

                setHasProformas(true)

                // Calculate totals from fetched proformas
                const proformasTotalNeto = fetchedProformas.reduce(
                  (acc, p) => acc + (p.totalData?.totalNetoSum || 0),
                  0
                )
                const proformasTotalVat = fetchedProformas.reduce(
                  (acc, p) => acc + (p.totalData?.totalVat || 0),
                  0
                )
                const proformasTotalSum = fetchedProformas.reduce(
                  (acc, p) => acc + (p.totalData?.totalSum || 0),
                  0
                )

                setProformasData({
                  proformaIds: editingInvoiceData.proformaReferenceIds,
                  proformasCodes: editingInvoiceData.proformaReferenceCodes || [],
                  orderIds: editingInvoiceData.orderReferenceId || [],
                  proformasTotalNeto,
                  proformasTotalVat,
                  proformasTotalSum,
                })
              })
              .catch((error) => {
                console.error("Error fetching proformas for editing:", error)
                if (!active) return
                // Fallback to empty totals
                setHasProformas(true)
                setProformasData({
                  proformaIds: editingInvoiceData.proformaReferenceIds,
                  proformasCodes: editingInvoiceData.proformaReferenceCodes || [],
                  orderIds: editingInvoiceData.orderReferenceId || [],
                  proformasTotalNeto: 0,
                  proformasTotalVat: 0,
                  proformasTotalSum: 0,
                })
              })
          } else {
            setHasProformas(false)
            setProformasData({})
          }
          // Potentially set showWeight based on a saved preference in editingInvoiceData
          if (typeof editingInvoiceData.showWeightInComment !== "undefined") {
            setShowWeight(editingInvoiceData.showWeightInComment)
          }
        } catch (error) {
          console.error("Error processing editingInvoiceData:", error)
          message.error("Error loading invoice data for editing.")
          setFromDrawer(false)
        } finally {
          setLoading(false)
        }

        // This part correctly sets showWeight for editing:
        if (typeof editingInvoiceData.showWeightInComment === "boolean") {
          // Check if it's specifically boolean
          setShowWeight(editingInvoiceData.showWeightInComment)
        } else {
          setShowWeight(false) // Default for old invoices or if field is undefined
        }
        setLoading(false)
      } else if (selectedRows && selectedRows.length > 0) {
        setLoading(true)
        try {
          // 1) Ensure client.id is defined for each order
          const updatedRows = selectedRows.map((item) => ({
            ...item,
            client: { ...item.client, id: item.clientId },
          }))

          // 2) Build newPrices array by branching on orderType
          const newPrices = updatedRows.map((item, index) => {
            const rollSum = parseFloat(((item.rollCount || 0) * item.price).toFixed(2))
            const rollVat = calculateVat(rollSum)

            if (item.orderType === "Tape") {
              // For Tape orders, use roll-related fields.
              return {
                key: index,
                code: 0,
                // Use the product name from your tape product structure.
                productType: item.selectedProduct.product.productType || "",
                product: item.selectedProduct.product.productName,
                unit: "pcs.",
                // For tapes, you might use a field like rollCount or a property from selectedProduct.
                count: item.rollCount || 0,
                price: item.price,
                // Sum is calculated as number of rolls * price per roll.
                sum: rollSum,
                vat: rollVat,
                dAccount: 2310,
                dName: "Norēķini ar pircējiem un pasūtītājiem",
                dCurrency: "Eur",
                cAccount: 6110,
                cName: "Ieņēmumi no pamatdarbības produkcijas un pakalpojumu pārdošanas",
                kCurrency: "Eur",
                hasProductComment: !!item.selectedProduct.advanced?.changableSpace,
                hasManualCount: false,
                hasChangableSpace: !!item.selectedProduct.advanced?.changableSpace,
                productComment: item.changableSpaceData || "", // Or a specific comment for tape
                rawComment: item.changableSpaceData || "",
              }
            } else {
              // Existing mapping for label orders.
              const hasChangableSpace = item.selectedProduct.advanced.changableSpace
              const price = item.price
              const baseCommentForNewLabelOrder = hasChangableSpace
                ? item.changableSpaceData || ""
                : ""

              const weight = item.weight ?? 0
              const boxesCount = item.boxesCount || 0
              const itemCount = forProforma ? item.labelCount : item.amountCreated
              const labelSum = parseFloat(((itemCount / 1000) * price).toFixed(2))
              const labelVat = calculateVat(labelSum)

              const baseComment = hasChangableSpace ? item.changableSpaceData : ""

              // Build the final comment
              let comment = baseComment

              return {
                key: index,
                code: 0,
                product: item.selectedProduct.product.productName,
                productType: item.selectedProduct.product.productType || "",
                unit: "thous.",
                count: itemCount,
                price,
                weight,
                boxesCount,
                sum: labelSum,
                vat: labelVat,
                dAccount: 2310,
                dName: "Norēķini ar pircējiem un pasūtītājiem",
                dCurrency: "Eur",
                cAccount: 6110,
                cName: "Ieņēmumi no pamatdarbības produkcijas un pakalpojumu pārdošanas",
                kCurrency: "Eur",
                productComment: baseCommentForNewLabelOrder,
                rawComment: baseCommentForNewLabelOrder,
                hasProductComment: Boolean(comment),
                hasManualCount: false,
                hasChangableSpace: hasChangableSpace,
              }
            }
          })

          // 3) Only for label orders, handle the "New Plate" row addition.
          updatedRows.forEach((item, index) => {
            if (
              item.orderType !== "Tape" &&
              item.selectedPlate &&
              item.selectedPlate.plateStatus === "new"
            ) {
              let totalPlateCost = calculateTotalPlateCost(item)
              if (totalPlateCost > 0) {
                const plateSum = parseFloat(totalPlateCost.toFixed(2))
                const plateVat = calculateVat(plateSum)
                let comments = []
                let print = item.selectedProduct.print
                if (item.selectedProduct.machine === "Arsoma") {
                  if (print.cmykColors > 1) comments.push(`CMYK: ${print.cmykColors}`)
                  if (print.coveringWCount > 1)
                    comments.push(`Covering White: ${print.coveringWCount}`)
                  if (print.goldOrSilver > 1) comments.push(`Gold/Silver: ${print.goldOrSilver}`)
                  if (print.pantoneColors > 1) comments.push(`Pantone: ${print.pantoneColors}`)
                  if (print.varnishUse > 1) comments.push(`Varnish`)
                  if (print.relief === "Yes") comments.push(`Relief`)
                } else if (item.selectedProduct.machine === "Viva") {
                  if (print.coveringWCount > 1)
                    comments.push(`Covering White: ${print.coveringWCount}`)
                  if (print.goldOrSilver > 1) comments.push(`Gold/Silver: ${print.goldOrSilver}`)
                  if (print.varnishUse > 1) comments.push(`Varnish`)
                  if (print.relief === "Yes") comments.push(`Relief`)
                  if (print.foilCount > 1) comments.push(`Foil : ${print.foilCount}`)
                }
                let productComment = comments.join(", ")
                newPrices.push({
                  ...newPrices[index],
                  key: newPrices.length,
                  unit: "pcs.",
                  count: 1,
                  product: "New Plate",
                  sum: parseFloat(totalPlateCost.toFixed(2)),
                  price: totalPlateCost,
                  vat: plateVat,
                  hasProductComment: comments.length > 0,
                  productComment: productComment,
                  rawComment: productComment,
                })
              }
            }
          })

          // 4) Save the new prices array and extract client/manufacturer info.
          setObjectFromDrawer(newPrices)
          setClientData(updatedRows[0].client)
          let manufacturer = updatedRows[0].manufacturer
          manufacturer.logoUrl =
            manufacturer.logoUrl ||
            "https://firebasestorage.googleapis.com/v0/b/managers-testing.appspot.com/o/manufacturers%2FdefaultLogo.png?alt=media&token=bdbe0c13-2de9-4be7-bc80-be8fd7e0f82a"
          setExtractedManufacturer(manufacturer)
          setFromDrawer(true)

          // 5) Check for proformas and fetch their data
          const fetchProformas = async () => {
            const fetchedProformas = []
            const uniqueProformaIds = new Set()
            const orderIds = []

            // Loop over selectedRows and only fetch each proforma once.
            for (const row of selectedRows) {
              // Collect orderIds regardless (or you can filter duplicates too, if needed)
              orderIds.push(row.id)

              // Check if the row has a proforma and a valid proformaId
              if (row.hasProforma && row.proforma?.proformaId) {
                const proformaId = row.proforma.proformaId
                // If this proformaId has not been seen before, fetch its document
                if (!uniqueProformaIds.has(proformaId)) {
                  uniqueProformaIds.add(proformaId)
                  try {
                    const docRef = doc(db, "proformas", proformaId)
                    const proformaSnap = await getDoc(docRef)
                    if (proformaSnap.exists()) {
                      fetchedProformas.push(proformaSnap.data())
                    }
                  } catch (error) {
                    console.error("Error fetching proforma with id:", proformaId, error)
                  }
                }
              }
            }

            setHasProformas(false)
            setProformasData({})

            if (fetchedProformas.length > 0) {
              setHasProformas(true)
              // Build the array of document codes from docPrefix and docNumber
              const proformasCodes = fetchedProformas.map((p) => `${p.docPrefix}${p.docNumber}`)
              // Calculate totals only from unique proformas (fetchedProformas array)
              const proformasTotalNeto = fetchedProformas.reduce(
                (acc, p) => acc + (p.totalData?.totalNetoSum || 0),
                0
              )
              const proformasTotalVat = fetchedProformas.reduce(
                (acc, p) => acc + (p.totalData?.totalVat || 0),
                0
              )
              const proformasTotalSum = fetchedProformas.reduce(
                (acc, p) => acc + (p.totalData?.totalSum || 0),
                0
              )

              // Update state including a new array of unique proformaIds
              setProformasData({
                proformaIds: Array.from(uniqueProformaIds),
                proformasCodes,
                orderIds,
                proformasTotalNeto,
                proformasTotalVat,
                proformasTotalSum,
              })
            }
          }

          // Execute the proforma fetching logic
          fetchProformas()
        } catch (error) {
          console.error("An error occurred:", error)
          setFromDrawer(false)
        } finally {
          setLoading(false)
        }

        setShowWeight(false)
      } else if (!open) {
        // Reset when drawer is closed and not for editing
        setObjectFromDrawer([])
        setClientData({})
        setExtractedManufacturer(null)
        setFromDrawer(false) // Default to false
        setHasProformas(false)
        setProformasData({})
        setShowWeight(false)
      }
    }

    loadData()
    return () => {
      active = false
    } // Cleanup
  }, [selectedRows, open, forProforma, editingInvoiceData])

  const displayRows = useMemo(() => {
    return objectFromDrawer.map((row) => {
      const baseComment = row.rawComment || ""
      if (showWeight && row.weight > 0) {
        const weightString = `${row.weight.toFixed(2)} kg`
        const finalComment = baseComment ? `${baseComment.trim()} | ${weightString}` : weightString
        return {
          ...row,
          productComment: finalComment.trim(),
          hasProductComment: Boolean(finalComment.trim()),
        }
      }
      return {
        ...row,
        productComment: baseComment.trim(),
        hasProductComment: Boolean(baseComment.trim()),
      }
    })
  }, [objectFromDrawer, showWeight])

  //right text
  const buttonText = forProforma ? "Proforma" : "Invoice"

  return (
    <div>
      <Button
        icon={<FileOutlined />}
        // always enabled if you’re editing
        disabled={!selectedRows || selectedRows.length === 0}
        onClick={showDrawer}>
        Create {buttonText}
      </Button>
      <Drawer
        title="Document Creation"
        width={1300}
        placement="right"
        onClose={onClose}
        destroyOnClose={true}
        open={open}>
        <CreationForm
          open={open}
          setOpen={setOpen}
          objectFromDrawer={displayRows}
          extractedManufacturer={extractedManufacturer}
          extractedClient={clientData}
          fromDrawer={fromDrawer}
          selectedRowKeys={selectedRowKeys}
          priceLoading={loading}
          resetRows={resetRows}
          forProforma={forProforma}
          hasProformas={hasProformas}
          proformasData={proformasData}
          setShowWeight={setShowWeight}
          showWeight={showWeight}
          setObjectFromDrawer={setObjectFromDrawer}
          setExtractedManufacturer={setExtractedManufacturer}
          setFromDrawer={setFromDrawer}
          editingInvoiceData={editingInvoiceData}
          onSuccess={(refresh) => {
            setOpen(false) // Close this drawer
            if (onDrawerClose) {
              onDrawerClose(refresh) // Notify parent, pass refresh flag
            }
          }}
        />
      </Drawer>
    </div>
  )
}
